import React, { Children } from 'react';
import { Users, DollarSign, ShieldCheck, Award, Utensils } from 'lucide-react';
import { motion } from 'framer-motion';
export function WhyFuntastic() {
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <section id="why-us" className="py-20 md:py-32 px-6 bg-white" data-id="element-677">
      <div className="max-w-7xl mx-auto" data-id="element-678">
        <div className="mb-16 max-w-3xl" data-id="element-679">
          <h2 className="text-3xl md:text-5xl font-extrabold text-navy mb-6 tracking-tight" data-id="element-680">
            Why Wyndham Vale families choose us over the chain centres.
          </h2>
        </div>

        <motion.div variants={container} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-681">
          {/* Card 1 - Merged "Meet the Family" */}
          <motion.div variants={item} className="bg-white p-8 rounded-[24px] shadow-card hover:shadow-card-hover transition-all duration-300 border-l-8 border-blue flex flex-col h-full relative overflow-hidden" data-id="element-682">
            <div className="absolute -right-10 -top-10 w-32 h-32 bg-blue/5 rounded-full blur-2xl pointer-events-none" data-id="element-683"></div>
            <div className="flex items-center gap-4 mb-6 relative z-10" data-id="element-684">
              <div className="w-16 h-16 rounded-full bg-sand/50 border-2 border-blue flex items-center justify-center overflow-hidden shrink-0" data-id="element-685">
                <img src="https://images.unsplash.com/photo-1544717305-2782549b5136?w=200&q=80" alt="Sanjay - Centre Director" className="w-full h-full object-cover" data-id="element-686" />
              </div>
              <div data-id="element-687">
                <h3 className="text-xl font-extrabold text-navy" data-id="element-688">
                  Family-run, not a chain.
                </h3>
                <p className="text-sm font-bold text-blue" data-id="element-689">
                  Owner-operated by Sanjay
                </p>
              </div>
            </div>
            <p className="text-navy/80 font-medium leading-relaxed" data-id="element-690">
              Sanjay founded Funtastic Kidz in 2018. You'll see him at drop-off
              most weeks. The same person making decisions about your child's
              day is the person you can call directly.
            </p>
          </motion.div>

          {/* Card 2 */}
          <motion.div variants={item} className="bg-white p-8 rounded-[24px] shadow-card hover:shadow-card-hover transition-all duration-300 border-l-8 border-coral flex flex-col h-full" data-id="element-691">
            <div className="w-14 h-14 rounded-2xl bg-coral/10 flex items-center justify-center text-coral mb-6" data-id="element-692">
              <DollarSign size={28} strokeWidth={2.5} data-id="element-693" />
            </div>
            <h3 className="text-xl font-extrabold text-navy mb-3" data-id="element-694">
              $26-58 a day cheaper.
            </h3>
            <p className="text-navy/80 font-medium leading-relaxed" data-id="element-695">
              Our published rate is $118/day. The Wyndham Vale average is $144.
              We charge less because we don't carry national brand overhead.
            </p>
          </motion.div>

          {/* Card 3 */}
          <motion.div variants={item} className="bg-white p-8 rounded-[24px] shadow-card hover:shadow-card-hover transition-all duration-300 border-l-8 border-teal flex flex-col h-full" data-id="element-696">
            <div className="w-14 h-14 rounded-2xl bg-teal-light flex items-center justify-center text-teal mb-6" data-id="element-697">
              <ShieldCheck size={28} strokeWidth={2.5} data-id="element-698" />
            </div>
            <h3 className="text-xl font-extrabold text-navy mb-3" data-id="element-699">
              CCS approved + 3 Day Guarantee.
            </h3>
            <p className="text-navy/80 font-medium leading-relaxed" data-id="element-700">
              Subsidies apply automatically. As a participating centre, eligible
              families receive 3 subsidised days per week under the new 2026
              policy.
            </p>
          </motion.div>

          {/* Card 4 */}
          <motion.div variants={item} className="bg-white p-8 rounded-[24px] shadow-card hover:shadow-card-hover transition-all duration-300 border-l-8 border-yellow lg:col-start-1 lg:col-span-1 flex flex-col h-full" data-id="element-701">
            <div className="w-14 h-14 rounded-2xl bg-yellow/20 flex items-center justify-center text-yellow mb-6" data-id="element-702">
              <Award size={28} strokeWidth={2.5} className="text-yellow-600" data-id="element-703" />
            </div>
            <h3 className="text-xl font-extrabold text-navy mb-3" data-id="element-704">
              Meeting NQS.
            </h3>
            <p className="text-navy/80 font-medium leading-relaxed" data-id="element-705">
              Last regulatory visit September 2025. Police checks and Working
              With Children Checks on every educator.
            </p>
          </motion.div>

          {/* Card 5 */}
          <motion.div variants={item} className="bg-white p-8 rounded-[24px] shadow-card hover:shadow-card-hover transition-all duration-300 border-l-8 border-blue-deep lg:col-span-2 flex flex-col h-full" data-id="element-706">
            <div className="w-14 h-14 rounded-2xl bg-blue/10 flex items-center justify-center text-blue mb-6" data-id="element-707">
              <Utensils size={28} strokeWidth={2.5} data-id="element-708" />
            </div>
            <h3 className="text-xl font-extrabold text-navy mb-3" data-id="element-709">
              Fresh meals included.
            </h3>
            <p className="text-navy/80 font-medium leading-relaxed" data-id="element-710">
              Five meals a day prepared on-site: breakfast, morning tea, lunch,
              afternoon tea, evening snack. Dietary, cultural, and religious
              needs catered for.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}